import Cookies from "js-cookie";
import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SupportStyleTable from "../../Components/Table/SupportStyleTable";
import {
  fetchComplaintData,
  findSupport,
  findJuniorDevelopers,
  SupportAcceptTask,
  SeniortoJuniorAssignReq,
  JuniorToSeniorAssignRatingReq,
  SupportHoldTask,
  checkingAfterImplementationReq,
} from "../../Redux/Slice/TaskMasterSlice";
import Loader from "../../Components/Loader/Loader";
import socket from "../../socket";
import {
  Calendar,
  Clock,
  CheckCircle2,
  CheckCheck,
  Eye,
  UserPlus,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import "./InhouseRnd.css";

const DeveloperAvatar = ({ photo, name, size = "w-8 h-8" }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [photo]);

  const getInitials = (n) => {
    if (!n) return "EM";
    const parts = n.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return n.slice(0, 2).toUpperCase();
  };

  if (photo && String(photo).trim() !== "" && !hasError) {
    return (
      <img
        src={photo}
        alt={name || "Developer"}
        className={`${size} rounded-full object-cover shrink-0 border border-slate-200 shadow-xs`}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full shrink-0 border border-slate-200 bg-blue-100 text-[#112A47] flex items-center justify-center text-[11px] font-bold tracking-tight uppercase shadow-xs`}
    >
      {getInitials(name)}
    </div>
  );
};

const SearchableDropdown = ({ options, value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter(
    (opt) =>
      (opt.label || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (opt.Role || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = (options || []).find((opt) => String(opt._id) === String(value));

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center justify-between">
        <span>{placeholder}</span>
        <span className="text-red-500 font-bold">*</span>
      </label>
      <div
        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl cursor-pointer flex justify-between items-center hover:border-[#112A47] focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 transition-all shadow-sm min-h-[42px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {selectedOption ? (
            <>
              <DeveloperAvatar photo={selectedOption.ProfilePhoto} name={selectedOption.label} size="w-7 h-7" />
              <div className="flex flex-col min-w-0">
                <span className="text-slate-800 font-semibold text-sm truncate">
                  {selectedOption.label}
                </span>
                {selectedOption.Role && (
                  <span className="text-gray-500 font-medium text-xs truncate">
                    {selectedOption.Role}
                  </span>
                )}
              </div>
              {selectedOption.isPresent && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full shrink-0 ml-auto mr-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Present
                </span>
              )}
            </>
          ) : (
            <span className="text-slate-400 text-sm">Choose developer...</span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180 text-[#112A47]" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          <div className="p-2 border-b border-slate-100 bg-slate-50/80">
            <input
              type="text"
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:border-[#112A47] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-400"
              placeholder={`Search ${placeholder}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1 divide-y divide-slate-50">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt._id}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors rounded-lg my-0.5 ${String(value) === String(opt._id)
                    ? "bg-blue-50 text-[#112A47] font-semibold"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                    }`}
                  onClick={() => {
                    onChange(opt._id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <DeveloperAvatar photo={opt.ProfilePhoto} name={opt.label} size="w-8 h-8" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="truncate leading-tight font-medium text-slate-800 text-xs">
                        {opt.label}
                      </span>
                      {opt.isPresent && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          Present
                        </span>
                      )}
                    </div>
                    {opt.Role && (
                      <span className="text-[10px] text-slate-400 font-normal leading-tight truncate">
                        {opt.Role}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-xs text-slate-400 text-center italic">No developers found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Avatar & Initials helpers for Company Names
const getCompanyInitials = (str) => {
  if (!str) return "DS";
  const parts = str.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return str.slice(0, 2).toUpperCase();
};

const getCompanyGradient = (str) => {
  const colors = [
    "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    "linear-gradient(135deg, #059669 0%, #047857 100%)",
    "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
    "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  ];
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const CustomerReq = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Active Tab state persisted in sessionStorage
  const [activeTab, setActiveTab] = useState(() => {
    const s = sessionStorage.getItem("customerReqActiveTab");
    return s && s !== "all" ? s : "queue";
  });

  useEffect(() => {
    sessionStorage.setItem("customerReqActiveTab", activeTab);
  }, [activeTab]);

  // Live Current Date & Time Clock
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatLiveDate = (d) => {
    return d.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatLiveTime = (d) => {
    return d.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).toUpperCase();
  };

  let parsedData = null;
  try {
    const raw = Cookies.get("loginData");
    if (raw) parsedData = JSON.parse(raw);
  } catch (e) {
    console.error("Error parsing loginData:", e);
  }

  const loggedInUserId = parsedData?.data?._id || parsedData?.user?._id || parsedData?._id;

  useEffect(() => {
    const handleUpdate = (data) => {
      console.log("Live customer req update received:", data);
      if (loggedInUserId) {
        dispatch(fetchComplaintData(loggedInUserId));
      }
    };
    socket.on("customerReqUpdate", handleUpdate);

    return () => {
      socket.off("customerReqUpdate", handleUpdate);
    };
  }, [dispatch, loggedInUserId]);

  const { ComplaintData, DataFindSupport, JuniorDevelopers } = useSelector(
    (state) => state.TaskMaster
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isJuniorModalOpen, setIsJuniorModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [recordData, setRecordData] = useState([]);

  const [selectedSupportId, setSelectedSupportId] = useState("");
  const [selectedJuniorId, setSelectedJuniorId] = useState("");
  const [isJuniorToSeniorModalOpen, setIsJuniorToSeniorModalOpen] = useState(false);
  const [selectedSeniorId, setSelectedSeniorId] = useState("");
  const [description, setDescription] = useState("");

  const [isPostCheckModalOpen, setIsPostCheckModalOpen] = useState(false);
  const [selectedPostCheckMemberId, setSelectedPostCheckMemberId] = useState("");
  const [postCheckDescription, setPostCheckDescription] = useState("");
  const [postCheckCodeUpdate, setPostCheckCodeUpdate] = useState("");
  const [postCheckTaskId, setPostCheckTaskId] = useState(null);
  const [postCheckTaskToken, setPostCheckTaskToken] = useState("");

  useEffect(() => {
    if (loggedInUserId) {
      dispatch(fetchComplaintData(loggedInUserId));
    }
    dispatch(findSupport());
    dispatch(findJuniorDevelopers());
  }, [dispatch, loggedInUserId]);

  const SeniorOptions = useMemo(() => {
    const map = new Map();
    const seenNames = new Set();

    const addMember = (m, defaultRole) => {
      if (!m || !m._id) return;
      if (loggedInUserId && String(m._id) === String(loggedInUserId)) return;
      if (m.Profile && String(m.Profile).toLowerCase() === "off") return;
      if (m.profile && String(m.profile).toLowerCase() === "off") return;
      if (m.Status && String(m.Status).toLowerCase() === "off") return;
      if (m.status && String(m.status).toLowerCase() === "off") return;
      if (m.isActive === false || m.active === false) return;

      const role = (m.Role || defaultRole || "").trim();
      const roleLower = role.toLowerCase();
      if (roleLower.includes("owner") || roleLower.includes("management")) return;

      const isDelphiSenior =
        roleLower.includes("delphi") &&
        (roleLower.includes("senior") || roleLower.includes("lead"));
      const isSupport = roleLower.includes("support");

      if (!isDelphiSenior && !isSupport) return;

      const name = (m.Name || "").trim();
      const nameLower = name.toLowerCase();
      if (!name || nameLower.includes("dynamic management")) return;
      const nameKey = name.toLowerCase();
      const idKey = String(m._id);

      if (!map.has(idKey) && !seenNames.has(nameKey)) {
        seenNames.add(nameKey);
        map.set(idKey, {
          label: name,
          _id: m._id,
          ProfilePhoto: m.ProfilePhoto || null,
          Role: role || defaultRole,
          isPresent: Boolean(m.isPresent),
          punchInTime: m.punchInTime || null,
        });
      }
    };

    if (Array.isArray(JuniorDevelopers?.data)) {
      JuniorDevelopers.data.forEach((d) => addMember(d, "Developer"));
    }
    if (Array.isArray(DataFindSupport?.data)) {
      DataFindSupport.data.forEach((s) => addMember(s, "Customer Support"));
    }

    return Array.from(map.values()).sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [JuniorDevelopers?.data, DataFindSupport?.data, loggedInUserId]);

  const supportingOptions = useMemo(() => {
    const list = [];
    const seenNames = new Set();
    (DataFindSupport?.data || []).forEach((support) => {
      if (support?.Profile && String(support.Profile).toLowerCase() === "off") return;
      if (support?.profile && String(support.profile).toLowerCase() === "off") return;
      if (support?.Status && String(support.Status).toLowerCase() === "off") return;
      if (support?.status && String(support.status).toLowerCase() === "off") return;
      if (support?.isActive === false || support?.active === false) return;
      const role = (support?.Role || "").toLowerCase();
      const name = (support?.Name || "").trim();
      const nameKey = name.toLowerCase();
      if (
        support?._id !== loggedInUserId &&
        !role.includes("owner") && !role.includes("management") && !nameKey.includes("dynamic management") && name &&
        !seenNames.has(nameKey)
      ) {
        seenNames.add(nameKey);
        list.push({
          label: support?.Name,
          _id: support?._id,
          ProfilePhoto: support?.ProfilePhoto,
          Role: support?.Role,
          isPresent: Boolean(support?.isPresent),
          punchInTime: support?.punchInTime,
        });
      }
    });
    return list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [DataFindSupport?.data, loggedInUserId]);

  const juniorOptions = useMemo(() => {
    const map = new Map();
    const seenNames = new Set();

    const addMember = (m, defaultRole) => {
      if (!m || !m._id) return;
      if (loggedInUserId && String(m._id) === String(loggedInUserId)) return;
      if (m.Profile && String(m.Profile).toLowerCase() === "off") return;
      if (m.profile && String(m.profile).toLowerCase() === "off") return;
      if (m.Status && String(m.Status).toLowerCase() === "off") return;
      if (m.status && String(m.status).toLowerCase() === "off") return;
      if (m.isActive === false || m.active === false) return;

      const role = (m.Role || defaultRole || "").trim();
      const roleLower = role.toLowerCase();
      if (roleLower.includes("owner") || roleLower.includes("management") || roleLower.includes("support")) return;

      const name = (m.Name || "").trim();
      const nameLower = name.toLowerCase();
      if (!name || nameLower.includes("dynamic management")) return;
      const nameKey = name.toLowerCase();
      const idKey = String(m._id);

      if (!map.has(idKey) && !seenNames.has(nameKey)) {
        seenNames.add(nameKey);
        map.set(idKey, {
          label: name,
          _id: m._id,
          ProfilePhoto: m.ProfilePhoto || null,
          Role: role || defaultRole,
          isPresent: Boolean(m.isPresent),
          punchInTime: m.punchInTime || null,
        });
      }
    };

    if (Array.isArray(JuniorDevelopers?.data)) {
      JuniorDevelopers.data.forEach((d) => addMember(d, "Developer"));
    }

    return Array.from(map.values()).sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [JuniorDevelopers?.data, loggedInUserId]);

  const postCheckMemberOptions = useMemo(() => {
    const list = [];
    if (Array.isArray(DataFindSupport?.data)) {
      DataFindSupport.data.forEach((s) => {
        if (s?.Profile && String(s.Profile).toLowerCase() === "off") return;
        if (s?.profile && String(s.profile).toLowerCase() === "off") return;
        if (s?.Status && String(s.Status).toLowerCase() === "off") return;
        if (s?.status && String(s.status).toLowerCase() === "off") return;
        if (s?.isActive === false || s?.active === false) return;
        const role = (s?.Role || "").toLowerCase();
        if (!role.includes("owner") && !role.includes("management") && !(s?.Name || "").toLowerCase().includes("dynamic management")) {
          list.push({
            label: s?.Name,
            _id: s?._id,
            ProfilePhoto: s?.ProfilePhoto,
            Role: s?.Role || "Support",
            isPresent: Boolean(s?.isPresent),
            punchInTime: s?.punchInTime,
          });
        }
      });
    }
    if (Array.isArray(JuniorDevelopers?.data)) {
      JuniorDevelopers.data.forEach((d) => {
        if (d?.Profile && String(d.Profile).toLowerCase() === "off") return;
        const role = (d?.Role || "").toLowerCase();
        if (!role.includes("owner") && !role.includes("management") && !list.some((item) => String(item._id) === String(d._id))) {
          list.push({
            label: d?.Name,
            _id: d?._id,
            ProfilePhoto: d?.ProfilePhoto,
            Role: d?.Role || "Developer",
            isPresent: Boolean(d?.isPresent),
            punchInTime: d?.punchInTime,
          });
        }
      });
    }
    return list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [DataFindSupport?.data, JuniorDevelopers?.data]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = hours.toString().padStart(2, "0");

    return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const handleShowTask = (id) => {
    navigate(`/CustomerReq/CustomerrequustDetail/${id}`);
  };

  const handleRowClick = (row) => {
    const taskId = row?.MainId || row?._id || row?.id;
    if (taskId) {
      handleShowTask(taskId);
    }
  };

  const handleAcceptTask = (tid) => {
    const userId = loggedInUserId;

    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    toast.info("Accepting task...", { autoClose: 1500 });

    dispatch(SupportAcceptTask({ id: userId, tid }))
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Task accepted successfully!");
          dispatch(fetchComplaintData(userId));
        } else {
          toast.error("Failed to accept task.");
        }
      })
      .catch((err) => {
        toast.error("An error occurred while accepting task.");
      });
  };

  const handleHoldTask = (tid) => {
    const userId = loggedInUserId;

    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    toast.info("Holding task...", { autoClose: 1500 });

    dispatch(SupportHoldTask({ id: userId, tid }))
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Task put on hold successfully!");
          dispatch(fetchComplaintData(userId));
        } else {
          toast.error("Failed to hold task.");
        }
      })
      .catch((err) => {
        toast.error("An error occurred while holding task.");
      });
  };

  const handleSendToPostCheck = (tid, token, defaultMemberId, existingCodeUpdate) => {
    setPostCheckTaskId(tid);
    setPostCheckTaskToken(token || "");
    setSelectedPostCheckMemberId(defaultMemberId || "");
    setPostCheckDescription("");
    setPostCheckCodeUpdate(existingCodeUpdate || "");
    setIsPostCheckModalOpen(true);
  };

  const handleStoreRecord = (recordArray) => {
    if (Array.isArray(recordArray)) {
      const uniqueDevelopers = recordArray.filter(
        (item, index, self) =>
          index ===
          self.findIndex((r) => r.InHouseId?._id === item.InHouseId?._id)
      );

      const formatted = uniqueDevelopers.map((item) => ({
        DeveloperID: item.InHouseId?._id,
        DeveloperName: item.InHouseId?.Name,
      }));

      setRecordData(formatted);
    }
  };

  // --- High-Quality Cell Renderers (Support Dashboard Standards) ---
  const renderTokenCell = ({ value }) => (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full font-bold text-xs shadow-2xs"
      style={{
        backgroundColor: "#EFF6FF",
        color: "#1D4ED8",
        border: "1px solid #BFDBFE",
        letterSpacing: "0.01em",
      }}
    >
      #{value || "--"}
    </span>
  );

  const renderCompanyCell = ({ value }) => {
    const companyName = value || "--";
    const initials = getCompanyInitials(companyName);
    const bgGradient = getCompanyGradient(companyName);

    return (
      <div className="flex items-center gap-2 py-0.5">
        <span
          className="inline-flex justify-center items-center rounded-full font-bold text-white flex-shrink-0 text-[10px]"
          style={{
            width: "28px",
            height: "28px",
            minWidth: "28px",
            minHeight: "28px",
            background: bgGradient,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
            border: "1.5px solid rgba(255, 255, 255, 0.95)",
            lineHeight: 1,
          }}
        >
          {initials}
        </span>
        <span
          className="font-semibold text-slate-800 text-xs truncate max-w-[210px]"
          title={companyName}
        >
          {companyName}
        </span>
      </div>
    );
  };

  const renderDateCell = ({ value }) => {
    if (!value || value === "-") return <span className="text-slate-400">--</span>;
    const parts = String(value).split(" ");
    const dateStr = parts[0] || "--";
    const timeStr = parts.slice(1).join(" ");

    return (
      <div className="flex flex-col justify-center leading-tight whitespace-nowrap">
        <span className="text-slate-800 font-semibold inline-flex items-center gap-1 text-xs">
          <Calendar size={12} className="text-blue-600" />
          <span>{dateStr}</span>
        </span>
        {timeStr && (
          <span className="text-slate-400 inline-flex items-center gap-1 mt-0.5 text-[11px] font-mono font-medium">
            <Clock size={11} className="text-slate-400" />
            <span>{timeStr}</span>
          </span>
        )}
      </div>
    );
  };

  const renderPersonCell = ({ value }) => {
    if (!value || value === "-" || value === "null") {
      return <span className="text-slate-400 text-xs">--</span>;
    }
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 shadow-2xs whitespace-nowrap"
      >
        <span>👤</span>
        <span className="font-semibold text-slate-800">{value}</span>
      </span>
    );
  };

  const renderStatusCell = ({ value }) => {
    const status = value || "Pending";
    let bg = "#FEF3C7";
    let text = "#92400E";
    let border = "#FDE68A";
    let dot = "#D97706";

    const lower = status.toLowerCase();
    if (lower.includes("complete")) {
      bg = "#D1FAE5";
      text = "#065F46";
      border = "#A7F3D0";
      dot = "#10B981";
    } else if (lower.includes("checking after implementation")) {
      bg = "#ECFEFF";
      text = "#0E7490";
      border = "#A5F3FC";
      dot = "#06B6D4";
    } else if (lower.includes("forward for implementation") || lower.includes("implementation")) {
      bg = "#F3E8FF";
      text = "#6B21A8";
      border = "#E9D5FF";
      dot = "#A855F7";
    } else if (lower.includes("checking to support")) {
      bg = "#E0F2FE";
      text = "#0369A1";
      border = "#BAE6FD";
      dot = "#0284C7";
    } else if (lower.includes("accept") || lower.includes("running")) {
      bg = "#DBEAFE";
      text = "#1E40AF";
      border = "#BFDBFE";
      dot = "#2563EB";
    } else if (lower.includes("hold")) {
      bg = "#F5F3FF";
      text = "#6D28D9";
      border = "#DDD6FE";
      dot = "#8B5CF6";
    } else if (lower.includes("reject")) {
      bg = "#FEE2E2";
      text = "#991B1B";
      border = "#FECACA";
      dot = "#EF4444";
    }

    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shadow-2xs whitespace-nowrap"
        style={{
          backgroundColor: bg,
          color: text,
          border: `1px solid ${border}`,
        }}
      >
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ backgroundColor: dot }}
        ></span>
        <span>{status}</span>
      </span>
    );
  };

  const renderRequirementCell = ({ value }) => (
    <div
      className="text-xs text-slate-700 whitespace-normal break-words max-w-[320px] line-clamp-2 leading-relaxed"
      title={value || ""}
    >
      {value || "-"}
    </div>
  );

  // --- Table Column Definitions with Support Dashboard Button Styles ---
  const PendingColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignName", headerName: "ASSIGN NAME", minWidth: 180, renderCell: renderPersonCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 250, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "AssignRequest",
      headerName: "ASSIGN REQUEST",
      width: 135,
      minWidth: 130,
      maxWidth: 145,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-assign shadow-xs"
          onClick={() => {
            setSelectedTaskId({ MainId: params.row.MainId });
            setIsJuniorModalOpen(true);
          }}
        >
          <UserPlus size={13} />
          <span>Assign Request</span>
        </button>
      ),
    },
    {
      field: "acceptTask",
      headerName: "ACCEPT TASK",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-accept shadow-xs"
          onClick={() => handleAcceptTask(params.row.MainId)}
        >
          <CheckCircle2 size={13} />
          <span>Accept Task</span>
        </button>
      ),
    },
  ];

  const PendingRows = useMemo(() => {
    return (
      ComplaintData?.Pending?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        CompanyId: el.CompanyId?.CompanyName,
        Requirement: el?.Requirement,
        PresentStatus: el?.PresentStatus,
      })) || []
    );
  }, [ComplaintData?.Pending]);

  const HoldingColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignName", headerName: "ASSIGN NAME", minWidth: 180, renderCell: renderPersonCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 220, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "acceptTask",
      headerName: "ACCEPT TASK",
      width: 125,
      minWidth: 120,
      maxWidth: 135,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-accept shadow-xs"
          onClick={() => handleAcceptTask(params.row.MainId)}
        >
          <CheckCircle2 size={13} />
          <span>Accept Task</span>
        </button>
      ),
    },
  ];

  const HoldingRows = useMemo(() => {
    return (
      ComplaintData?.Hold?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        Requirement: el?.Requirement,
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
      })) || []
    );
  }, [ComplaintData?.Hold]);

  const RunningColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignName", headerName: "ASSIGN NAME", minWidth: 180, renderCell: renderPersonCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 220, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "holdTask",
      headerName: "HOLD TASK",
      width: 110,
      minWidth: 105,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-hold shadow-xs"
          onClick={() => handleHoldTask(params.row.MainId)}
        >
          <Clock size={13} />
          <span>Hold Task</span>
        </button>
      ),
    },
    {
      field: "AssignRequest",
      headerName: "ASSIGN REQUEST",
      width: 135,
      minWidth: 130,
      maxWidth: 145,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-assign shadow-xs"
          onClick={() => {
            setSelectedTaskId({ MainId: params.row.MainId });
            setIsJuniorModalOpen(true);
          }}
        >
          <UserPlus size={13} />
          <span>Assign Request</span>
        </button>
      ),
    },
    {
      field: "checkingTask",
      headerName: "CHECKING TASK",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-check shadow-xs"
          onClick={() => {
            handleStoreRecord(params.row.Record);
            setSelectedTaskId({ MainId: params.row.MainId });
            const defaultSeniorId = params.row.SeniorDeveloperId || "";
            setSelectedSeniorId(defaultSeniorId);
            setIsJuniorToSeniorModalOpen(true);
          }}
        >
          <CheckCircle2 size={13} />
          <span>Check Task</span>
        </button>
      ),
    },
  ];

  const RunningRows = useMemo(() => {
    return (
      ComplaintData?.Accept?.map((el, index) => {
        const seniorId =
          el?.SeniorDevloperID?._id ||
          (typeof el?.SeniorDevloperID === "string" ? el?.SeniorDevloperID : "");
        return {
          id: index + 1,
          tokan: el?.CallNo,
          MainId: el._id,
          CompanyName: el?.CompanyName?.WhaClientName,
          AssignDate: formatDate(el.Date),
          CompanyId: el.CompanyId?.CompanyName,
          Record: el.Record,
          AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
          SeniorDeveloperId: seniorId,
          SeniorDeveloperObj: el?.SeniorDevloperID,
          AssignName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
          Requirement: el?.Requirement,
          PresentStatus: el?.PresentStatus,
        };
      }) || []
    );
  }, [ComplaintData?.Accept]);

  const CompleteColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignName", headerName: "ASSIGN NAME", minWidth: 180, renderCell: renderPersonCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 300, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const CompleteRows = useMemo(() => {
    return (
      ComplaintData?.Complete?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        tokan: el?.CallNo,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignDate: formatDate(el.Date),
        CompanyId: el.CompanyId?.CompanyName,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        Requirement: el?.Requirement,
        PresentStatus: el?.PresentStatus,
      })) || []
    );
  }, [ComplaintData?.Complete]);

  const JuniorToSeniorColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "AssignRequest",
      headerName: "ASSIGN REQUEST",
      width: 135,
      minWidth: 130,
      maxWidth: 145,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-assign shadow-xs"
          onClick={() => {
            setSelectedTaskId({ MainId: params.row.MainId });
            setIsJuniorModalOpen(true);
          }}
        >
          <UserPlus size={13} />
          <span>Assign Request</span>
        </button>
      ),
    },
    {
      field: "checkingTask",
      headerName: "CHECKING TASK",
      width: 130,
      minWidth: 125,
      maxWidth: 140,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-check shadow-xs"
          onClick={() => {
            handleStoreRecord(params.row.Record);
            setSelectedTaskId({ MainId: params.row.MainId });
            const defaultSeniorId = params.row.SeniorDeveloperId || "";
            setSelectedSeniorId(defaultSeniorId);
            setIsJuniorToSeniorModalOpen(true);
          }}
        >
          <CheckCircle2 size={13} />
          <span>Check Task</span>
        </button>
      ),
    },
  ];

  const JuniorToSeniorRows = useMemo(() => {
    return (
      ComplaintData?.Juniortosen?.map((el, index) => {
        const seniorId =
          el?.SeniorDevloperID?._id ||
          (typeof el?.SeniorDevloperID === "string" ? el?.SeniorDevloperID : "");
        return {
          id: index + 1,
          tokan: el?.CallNo,
          MainId: el._id,
          CompanyName: el?.CompanyName?.WhaClientName,
          AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
          SeniorDeveloperId: seniorId,
          SeniorDeveloperObj: el?.SeniorDevloperID,
          AssignDate: formatDate(el.Date),
          CompanyId: el.CompanyId?.CompanyName,
          Record: el.Record,
          PresentStatus: el?.PresentStatus,
        };
      }) || []
    );
  }, [ComplaintData?.Juniortosen]);

  const SeniorToJuniorColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "CallNo", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "SuportTeamId", headerName: "DEVELOPER NAME", minWidth: 175, renderCell: renderPersonCell },
    { field: "Status", headerName: "STATUS", width: 115, minWidth: 110, maxWidth: 125, renderCell: renderStatusCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const SeniorToJuniorRows = useMemo(() => {
    return (
      ComplaintData?.Junior?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        CallNo: el?.CallNo,
        CompanyId: el.CompanyId?.CompanyName,
        CompanyName: el?.CompanyName?.WhaClientName,
        SuportTeamId: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        Status: el?.Status,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        PresentStatus: el?.PresentStatus,
      })) || []
    );
  }, [ComplaintData?.Junior]);

  const CheckingToSupportColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignName", headerName: "SUPPORT MEMBER", minWidth: 180, renderCell: renderPersonCell },
    { field: "Status", headerName: "STATUS", width: 155, minWidth: 145, maxWidth: 175, renderCell: renderStatusCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 220, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const CheckingToSupportRows = useMemo(() => {
    return (
      ComplaintData?.CheckingToSupport?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        Status: el?.Status || "Checking to Support",
        Requirement: el?.Requirement,
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
        Record: el?.Record,
      })) || []
    );
  }, [ComplaintData?.CheckingToSupport]);

  const ForwardImplementationColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignName", headerName: "ASSIGNED MEMBER", minWidth: 180, renderCell: renderPersonCell },
    { field: "Status", headerName: "STATUS", width: 175, minWidth: 160, maxWidth: 195, renderCell: renderStatusCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 220, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
    {
      field: "postCheckAction",
      headerName: "POST CHECK",
      width: 145,
      minWidth: 135,
      maxWidth: 160,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-check shadow-xs"
          onClick={() =>
            handleSendToPostCheck(
              params.row.MainId,
              params.row.tokan,
              params.row.AssignNameId,
              params.row.CodeUpdate
            )
          }
          title="Send for Checking after Implementation"
        >
          <CheckCircle2 size={13} />
          <span>Post-Check</span>
        </button>
      ),
    },
  ];

  const ForwardImplementationRows = useMemo(() => {
    const extractCodeUpdate = (el) => {
      if (el?.CodeUpdate && typeof el.CodeUpdate === "string" && el.CodeUpdate.trim()) {
        return el.CodeUpdate.trim();
      }
      if (Array.isArray(el?.Record)) {
        for (let i = el.Record.length - 1; i >= 0; i--) {
          const desc = el.Record[i]?.DescripTione || "";
          if (desc.includes("Code Update:")) {
            const parts = desc.split("Code Update:")[1];
            const text = parts ? parts.split("| Notes:")[0] : "";
            if (text && text.trim()) return text.trim();
          }
        }
      }
      return "";
    };

    return (
      ComplaintData?.ForwardImplementation?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        AssignNameId: el?.SuportTeamId?._id || el?.SuportTeamId || el?.SupportTeamId?._id || el?.SupportTeamId || "",
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        Status: el?.Status || "Forward for Implementation",
        Requirement: el?.Requirement,
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
        Record: el?.Record,
        CodeUpdate: extractCodeUpdate(el),
      })) || []
    );
  }, [ComplaintData?.ForwardImplementation]);

  const CheckingAfterImplementationColumns = [
    { field: "id", headerName: "ID", width: 50, minWidth: 45, maxWidth: 55 },
    { field: "tokan", headerName: "CALL NO.", width: 85, minWidth: 80, maxWidth: 90, renderCell: renderTokenCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 200, renderCell: renderCompanyCell },
    { field: "AssignedBy", headerName: "ASSIGNED BY", minWidth: 165, renderCell: renderPersonCell },
    { field: "AssignName", headerName: "SUPPORT MEMBER", minWidth: 180, renderCell: renderPersonCell },
    { field: "Status", headerName: "STATUS", width: 185, minWidth: 170, maxWidth: 205, renderCell: renderStatusCell },
    { field: "AssignDate", headerName: "ASSIGN DATE", minWidth: 135, renderCell: renderDateCell },
    { field: "Requirement", headerName: "REQUIREMENT", minWidth: 250, renderCell: renderRequirementCell },
    {
      field: "showTask",
      headerName: "SHOW TASK",
      width: 110,
      minWidth: 110,
      maxWidth: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="action-btn action-btn-show shadow-xs"
          onClick={() => handleShowTask(params.row.MainId)}
          title={`Present Status: ${params.row.PresentStatus || "N/A"}`}
        >
          <Eye size={13} />
          <span>Show Task</span>
        </button>
      ),
    },
  ];

  const CheckingAfterImplementationRows = useMemo(() => {
    return (
      ComplaintData?.CheckingAfterImplementation?.map((el, index) => ({
        id: index + 1,
        tokan: el?.CallNo,
        MainId: el._id,
        CompanyName: el?.CompanyName?.WhaClientName,
        AssignName: el?.SuportTeamId?.Name || el?.SupportTeamId?.Name,
        AssignedBy: el?.SeniorDevloperID?.Name || "Admin",
        AssignDate: formatDate(el.Date),
        Status: el?.Status || "Checking after Implementation",
        Requirement: el?.Requirement,
        CompanyId: el.CompanyId?.CompanyName,
        PresentStatus: el?.PresentStatus,
        Record: el?.Record,
      })) || []
    );
  }, [ComplaintData?.CheckingAfterImplementation]);

  if (
    !ComplaintData ||
    !DataFindSupport?.data ||
    !JuniorDevelopers?.data
  ) {
    return <Loader />;
  }

  return (
    <>
      <div className="w-full dashboard-page-container">
        {/* Support Dashboard Compact Banner with Clock & Badge */}
        <div className="page-header-banner-compact">
          <div>
            <h4 className="mb-0 font-bold">Customer Requirements Dashboard</h4>
            <span className="page-subtitle">
              Track, assign, and resolve customer requirement tasks and developer assignments
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Live Current Date & Time Clock */}
            <div className="dashboard-live-clock" title="Current Live Date & Time">
              <span className="clock-date">
                <Calendar size={14} className="text-blue-600" />
                <span>{formatLiveDate(currentDateTime)}</span>
              </span>
              <span className="clock-divider"></span>
              <span className="clock-time">
                <Clock size={14} className="text-blue-600" />
                <span>{formatLiveTime(currentDateTime)}</span>
                <span className="live-dot" title="Live Clock Running"></span>
              </span>
            </div>

            {/* Total Pending Count Badge */}
            <span
              className="px-2.5 py-1.5 font-bold text-xs rounded-lg shadow-xs flex items-center gap-1.5 bg-amber-400 text-amber-950"
            >
              Pending: {PendingRows.length}
            </span>
          </div>
        </div>

        {/* Status Tabs Bar */}
        <div className="dashboard-tabs-bar">
          <button
            type="button"
            className={`tab-btn-status tab-btn-queue ${activeTab === "queue" ? "active" : ""}`}
            onClick={() => setActiveTab("queue")}
          >
            <span className="tab-status-dot"></span>
            <span>In Queue Task</span>
            <span className="tab-badge">{PendingRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-holding ${activeTab === "holding" ? "active" : ""}`}
            onClick={() => setActiveTab("holding")}
          >
            <span className="tab-status-dot"></span>
            <span>Holding Task</span>
            <span className="tab-badge">{HoldingRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-running ${activeTab === "running" ? "active" : ""}`}
            onClick={() => setActiveTab("running")}
          >
            <span className="tab-status-dot"></span>
            <span>Running Task</span>
            <span className="tab-badge">{RunningRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-complete ${activeTab === "complete" ? "active" : ""}`}
            onClick={() => setActiveTab("complete")}
          >
            <span className="tab-status-dot"></span>
            <span>Complete Task</span>
            <span className="tab-badge">{CompleteRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-juniorsenior ${activeTab === "juniorsenior" ? "active" : ""}`}
            onClick={() => setActiveTab("juniorsenior")}
          >
            <span className="tab-status-dot"></span>
            <span>Junior To Senior</span>
            <span className="tab-badge">{JuniorToSeniorRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-seniorjunior ${activeTab === "seniorjunior" ? "active" : ""}`}
            onClick={() => setActiveTab("seniorjunior")}
          >
            <span className="tab-status-dot"></span>
            <span>Senior To Junior</span>
            <span className="tab-badge">{SeniorToJuniorRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-checksupport ${activeTab === "checksupport" ? "active" : ""}`}
            onClick={() => setActiveTab("checksupport")}
          >
            <span className="tab-status-dot"></span>
            <span>Checking to Support</span>
            <span className="tab-badge">{CheckingToSupportRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-forwardimpl ${activeTab === "forwardimpl" ? "active" : ""}`}
            onClick={() => setActiveTab("forwardimpl")}
          >
            <span className="tab-status-dot"></span>
            <span>Forward for Implementation</span>
            <span className="tab-badge">{ForwardImplementationRows.length}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-checkafterimpl ${activeTab === "checkafterimpl" ? "active" : ""}`}
            onClick={() => setActiveTab("checkafterimpl")}
          >
            <span className="tab-status-dot"></span>
            <span>Checking after Implementation</span>
            <span className="tab-badge">{CheckingAfterImplementationRows.length}</span>
          </button>
        </div>

        {/* Main Table Area Filling Remaining Screen Height */}
        <div className="w-full flex-grow flex flex-col min-h-0 overflow-hidden">
          {/* Tab 1: In Queue Task */}
          {activeTab === "queue" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #f59e0b" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-amber-600">
                    In Queue Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    New pending customer requests awaiting developer assignment or pickup
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {PendingRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_queue"
                  fileName="CustomerReq_InQueue_Tasks"
                  columns={PendingColumns}
                  rows={PendingRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No in-queue tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 2: Holding Task */}
          {activeTab === "holding" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #6366f1" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-indigo-600">
                    Holding Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Customer tasks placed on temporary hold
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {HoldingRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_holding"
                  fileName="CustomerReq_Holding_Tasks"
                  columns={HoldingColumns}
                  rows={HoldingRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No holding tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 3: Running Task */}
          {activeTab === "running" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #1d4ed8" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-blue-600">
                    Running Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Currently active customer requirement tasks
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {RunningRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_running"
                  fileName="CustomerReq_Running_Tasks"
                  columns={RunningColumns}
                  rows={RunningRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No running tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 4: Complete Task */}
          {activeTab === "complete" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #15803d" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-emerald-600">
                    Complete Task
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Successfully finished and closed customer requirement tasks
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {CompleteRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_complete"
                  fileName="CustomerReq_Complete_Tasks"
                  columns={CompleteColumns}
                  rows={CompleteRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No completed tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 5: Junior To Senior Task List */}
          {activeTab === "juniorsenior" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #7e22ce" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-purple-600">
                    Junior To Senior Task List
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks submitted by junior developers awaiting senior verification
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {JuniorToSeniorRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_juniorsenior"
                  fileName="CustomerReq_JuniorToSenior_Tasks"
                  columns={JuniorToSeniorColumns}
                  rows={JuniorToSeniorRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No junior to senior tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 6: Senior To Junior Task List */}
          {activeTab === "seniorjunior" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #0d9488" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-teal-600">
                    Senior To Junior Task List
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks delegated by seniors to junior developers
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {SeniorToJuniorRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_seniorjunior"
                  fileName="CustomerReq_SeniorToJunior_Tasks"
                  columns={SeniorToJuniorColumns}
                  rows={SeniorToJuniorRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No senior to junior tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 7: Checking to Support */}
          {activeTab === "checksupport" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #0284c7" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-sky-600">
                    Checking to Support
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks reviewed by senior and awaiting support verification
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {CheckingToSupportRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_checksupport"
                  fileName="CustomerReq_CheckingToSupport_Tasks"
                  columns={CheckingToSupportColumns}
                  rows={CheckingToSupportRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No checking to support tasks found."
                />
              </div>
            </div>
          )}

          {/* Tab 8: Forward for Implementation */}
          {activeTab === "forwardimpl" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #7e22ce" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-purple-600">
                    Forward for Implementation
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks currently deployed for client server/EXE implementation
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {ForwardImplementationRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_forwardimpl"
                  fileName="CustomerReq_Forward_Implementation_Tasks"
                  columns={ForwardImplementationColumns}
                  rows={ForwardImplementationRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No tasks in forward for implementation."
                />
              </div>
            </div>
          )}

          {/* Tab 9: Checking after Implementation */}
          {activeTab === "checkafterimpl" && (
            <div className="inhouse-card h-full">
              <div
                className="inhouse-card-header"
                style={{ borderLeft: "4px solid #0891b2" }}
              >
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-cyan-600">
                    Checking after Implementation
                  </span>
                  <span className="text-slate-500 text-xs font-medium">
                    Tasks with implementation finished, under final post-check before completion
                  </span>
                </div>
                <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
                  Total: {CheckingAfterImplementationRows.length}
                </span>
              </div>
              <div className="inhouse-card-body">
                <SupportStyleTable
                  tableId="customerreq_checkafterimpl"
                  fileName="CustomerReq_CheckingAfterImplementation_Tasks"
                  columns={CheckingAfterImplementationColumns}
                  rows={CheckingAfterImplementationRows}
                  fullHeight={true}
                  onRowClick={handleRowClick}
                  emptyComponent="No tasks in checking after implementation."
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Supporting Person */}
      {isModalOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-md p-6 border border-slate-100">
            <h2 className="text-lg font-bold mb-4 text-[#112A47]">
              Select Supporting Person
            </h2>

            <div className="mb-4">
              <SearchableDropdown
                options={supportingOptions}
                value={selectedSupportId}
                onChange={(id) => setSelectedSupportId(id)}
                placeholder="Supporting Person"
              />
            </div>

            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-[#112A47]/20 focus:border-[#112A47]"
              rows={3}
            ></textarea>

            {recordData?.length > 0 && (
              <div className="border border-slate-200 rounded-xl p-4 mb-4 bg-slate-50/50">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
                  Developers Involved
                </h3>

                <div className="space-y-3">
                  {recordData.map((dev, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border-b border-slate-100 pb-2"
                    >
                      <div>
                        <p className="text-slate-800 font-semibold text-xs">
                          {dev.DeveloperName}
                        </p>
                      </div>

                      {/* Rating Input */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <span
                            key={i}
                            onClick={() => {
                              setRecordData((prev) => {
                                const currentTotal = prev.reduce(
                                  (sum, d) => sum + (d.rating || 0),
                                  0
                                );
                                const currentRating = prev[index].rating || 0;
                                const newRating = i + 1;
                                const totalAfterChange =
                                  currentTotal - currentRating + newRating;

                                if (totalAfterChange > 10) {
                                  alert("Total rating cannot exceed 10!");
                                  return prev;
                                }

                                return prev.map((d, idx) =>
                                  idx === index
                                    ? { ...d, rating: newRating }
                                    : d
                                );
                              });
                            }}
                            className={`cursor-pointer text-base ${i < (dev.rating || 0)
                              ? "text-amber-400"
                              : "text-slate-200"
                              }`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="ml-1 text-xs font-semibold text-slate-600">
                          {dev.rating || 0}/10
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 text-right text-xs text-slate-600">
                  Total Rating:{" "}
                  <span className="font-bold text-slate-800">
                    {recordData.reduce((sum, d) => sum + (d.rating || 0), 0)}
                    /10
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2.5 pt-2 border-t border-slate-100">
              <button
                type="button"
                className="px-4 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedSupportId("");
                  setSelectedTaskId(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-5 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  !selectedSupportId ||
                  recordData.reduce((sum, d) => sum + (d.rating || 0), 0) !== 10
                }
                onClick={() => {
                  const total = recordData.reduce(
                    (sum, d) => sum + (d.rating || 0),
                    0
                  );

                  if (total !== 10) {
                    toast.error(
                      "Please ensure the total rating equals exactly 10 before proceeding!"
                    );
                    return;
                  }

                  dispatch(
                    JuniorToSeniorAssignRatingReq({
                      complainBoxId: selectedTaskId?.MainId || selectedTaskId?._id,
                      SuportTeamId: selectedSupportId,
                      SupportTeamId: selectedSupportId,
                      ratings: recordData,
                      SeniorDevloperID: loggedInUserId,
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task assigned successfully!");
                      if (loggedInUserId) {
                        dispatch(fetchComplaintData(loggedInUserId));
                      }
                    } else {
                      toast.error("Failed to assign task.");
                    }
                  });
                  setIsModalOpen(false);
                  setSelectedSupportId("");
                  setSelectedTaskId(null);
                  setDescription("");
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>,
        document.body)}

      {/* Modal for Junior → Senior Assignment */}
      {isJuniorToSeniorModalOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-[90%] max-w-md p-6 border border-slate-100">
            <h2 className="text-lg font-bold mb-4 text-[#112A47]">
              Check Task Assignment
            </h2>

            <div className="mb-4">
              <SearchableDropdown
                options={SeniorOptions}
                value={selectedSeniorId}
                onChange={(id) => setSelectedSeniorId(id)}
                placeholder="Select Developer / Support Member"
              />
            </div>

            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-[#112A47]/20 focus:border-[#112A47]"
              rows={3}
            ></textarea>

            {recordData?.length > 0 && (
              <div className="border border-slate-200 rounded-xl p-4 mb-4 bg-slate-50/50">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
                  Developers Involved
                </h3>

                <div className="space-y-3">
                  {recordData.map((dev, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border-b border-slate-100 pb-2"
                    >
                      <div>
                        <p className="text-slate-800 font-semibold text-xs">
                          {dev.DeveloperName}
                        </p>
                      </div>

                      <div className="flex items-center space-x-1">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <span
                            key={i}
                            onClick={() => {
                              setRecordData((prev) => {
                                const currentTotal = prev.reduce(
                                  (sum, d) => sum + (d.rating || 0),
                                  0
                                );
                                const currentRating = prev[index].rating || 0;
                                const newRating = i + 1;
                                const totalAfterChange =
                                  currentTotal - currentRating + newRating;

                                if (totalAfterChange > 10) {
                                  alert("Total rating cannot exceed 10!");
                                  return prev;
                                }

                                return prev.map((d, idx) =>
                                  idx === index
                                    ? { ...d, rating: newRating }
                                    : d
                                );
                              });
                            }}
                            className={`cursor-pointer text-base ${i < (dev.rating || 0)
                              ? "text-amber-400"
                              : "text-slate-200"
                              }`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="ml-1 text-xs font-semibold text-slate-600">
                          {dev.rating || 0}/10
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 text-right text-xs text-slate-600">
                  Total Rating:{" "}
                  <span className="font-bold text-slate-800">
                    {recordData.reduce((sum, d) => sum + (d.rating || 0), 0)}
                    /10
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2.5 pt-2 border-t border-slate-100">
              <button
                type="button"
                className="px-4 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition"
                onClick={() => {
                  setIsJuniorToSeniorModalOpen(false);
                  setSelectedSeniorId("");
                  setSelectedTaskId(null);
                  setDescription("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-5 py-1.5 text-xs font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  !selectedSeniorId ||
                  recordData.reduce((sum, d) => sum + (d.rating || 0), 0) !== 10
                }
                onClick={() => {
                  const total = recordData.reduce(
                    (sum, d) => sum + (d.rating || 0),
                    0
                  );

                  if (total !== 10) {
                    toast.error(
                      "Please ensure the total rating equals exactly 10 before proceeding!"
                    );
                    return;
                  }

                  dispatch(
                    JuniorToSeniorAssignRatingReq({
                      complainBoxId: selectedTaskId?.MainId || selectedTaskId?._id,
                      SuportTeamId: selectedSeniorId,
                      SupportTeamId: selectedSeniorId,
                      ratings: recordData,
                      SeniorDevloperID: loggedInUserId,
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task assigned to senior successfully!");
                      if (loggedInUserId) {
                        dispatch(fetchComplaintData(loggedInUserId));
                      }
                    } else {
                      toast.error("Failed to assign task to senior.");
                    }
                  });

                  setIsJuniorToSeniorModalOpen(false);
                  setSelectedSeniorId("");
                  setSelectedTaskId(null);
                  setDescription("");
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>,
        document.body)}

      {/* Modal for Junior Developer */}
      {isJuniorModalOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-200 overflow-y-auto"
          onClick={() => {
            setIsJuniorModalOpen(false);
            setSelectedJuniorId("");
            setSelectedTaskId(null);
            setDescription("");
          }}
        >
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-100 flex flex-col overflow-visible animate-in fade-in zoom-in-95 duration-200 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-3.5 bg-gradient-to-r from-[#112A47] to-[#1c3a60] text-white flex items-center justify-between shadow-sm rounded-t-2xl">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center text-white border border-white/15 shadow-inner">
                  <UserPlus size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold tracking-wide text-white leading-tight">
                    Select Junior Developer
                  </h2>
                  <p className="text-[11px] text-blue-200/90 font-normal">
                    Assign task to junior developer
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsJuniorModalOpen(false);
                  setSelectedJuniorId("");
                  setSelectedTaskId(null);
                  setDescription("");
                }}
                className="text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors focus:outline-none"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Junior Developer <span className="text-rose-500">*</span>
                </label>
                <SearchableDropdown
                  options={juniorOptions}
                  value={selectedJuniorId}
                  onChange={(id) => setSelectedJuniorId(id)}
                  placeholder="Choose developer..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Instructions / Description
                  </label>
                  <span className="text-[11px] text-slate-600 font-normal">
                    (Optional)
                  </span>
                </div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter instructions or notes for junior developer..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#112A47]/20 focus:border-[#112A47] transition-all resize-none placeholder:text-slate-600 text-slate-700"
                  rows={3}
                ></textarea>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2.5 rounded-b-2xl">
              <button
                type="button"
                onClick={() => {
                  setIsJuniorModalOpen(false);
                  setSelectedJuniorId("");
                  setSelectedTaskId(null);
                  setDescription("");
                }}
                className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-600 font-medium text-xs transition-colors focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedJuniorId}
                className="px-4 py-2 rounded-xl bg-[#112A47] hover:bg-[#0c2545] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs shadow-md shadow-slate-900/10 transition-all focus:outline-none flex items-center gap-1.5"
                onClick={() => {
                  const seniorId = loggedInUserId;

                  dispatch(
                    SeniortoJuniorAssignReq({
                      SuportTeamId: selectedJuniorId,
                      SupportTeamId: selectedJuniorId,
                      complainBoxId: selectedTaskId?.MainId || selectedTaskId?._id,
                      DescripTione: description,
                      SeniorDevloperID: seniorId,
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task assigned to Junior Developer successfully!");
                      if (seniorId) {
                        dispatch(fetchComplaintData(seniorId));
                      }
                    } else {
                      toast.error("Failed to assign task. Please try again.");
                    }
                  }).catch((err) => {
                    console.error("Assignment error:", err);
                    toast.error("An error occurred during task assignment.");
                  });

                  setIsJuniorModalOpen(false);
                  setSelectedJuniorId("");
                  setSelectedTaskId(null);
                  setDescription("");
                }}
              >
                <CheckCircle2 size={16} />
                <span>Assign Task</span>
              </button>
            </div>
          </div>
        </div>,
        document.body)}

      {/* Modal for Post-Check / Checking after Implementation */}
      {isPostCheckModalOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => {
            setIsPostCheckModalOpen(false);
            setSelectedPostCheckMemberId("");
            setPostCheckTaskId(null);
            setPostCheckDescription("");
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md border border-slate-100 overflow-visible transform transition-all animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-3.5 bg-gradient-to-r from-[#0284c7] to-[#0369a1] text-white flex items-center justify-between shadow-sm rounded-t-2xl">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center text-white border border-white/15 shadow-inner">
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold tracking-wide text-white leading-tight">
                    Checking after Implementation
                  </h2>
                  <p className="text-[11px] text-sky-100/90 font-normal">
                    {postCheckTaskToken ? `Token No: #${postCheckTaskToken}` : "Assign verification to team member"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsPostCheckModalOpen(false);
                  setSelectedPostCheckMemberId("");
                  setPostCheckTaskId(null);
                  setPostCheckDescription("");
                }}
                className="text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors focus:outline-none"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Select Member for Verification <span className="text-rose-500">*</span>
                </label>
                <SearchableDropdown
                  options={postCheckMemberOptions}
                  value={selectedPostCheckMemberId}
                  onChange={(id) => setSelectedPostCheckMemberId(id)}
                  placeholder="Choose member..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Instructions / Notes
                  </label>
                  <span className="text-[11px] text-slate-500 font-normal">
                    (Optional)
                  </span>
                </div>
                <textarea
                  value={postCheckDescription}
                  onChange={(e) => setPostCheckDescription(e.target.value)}
                  placeholder="Enter notes for checking (e.g. EXE updated, please test)..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0284c7]/20 focus:border-[#0284c7] transition-all resize-none placeholder:text-slate-400 text-slate-700"
                  rows={2}
                ></textarea>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    What is updated in this code? <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <span className="text-[11px] text-rose-500 font-semibold">
                    (Compulsory)
                  </span>
                </div>
                <textarea
                  value={postCheckCodeUpdate}
                  onChange={(e) => setPostCheckCodeUpdate(e.target.value)}
                  placeholder="Enter what changes/updates were made in code (Required)..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0284c7]/20 focus:border-[#0284c7] transition-all resize-none placeholder:text-slate-400 text-slate-700 font-medium"
                  rows={3}
                  required
                ></textarea>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2.5 rounded-b-2xl">
              <button
                type="button"
                onClick={() => {
                  setIsPostCheckModalOpen(false);
                  setSelectedPostCheckMemberId("");
                  setPostCheckTaskId(null);
                  setPostCheckDescription("");
                  setPostCheckCodeUpdate("");
                }}
                className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-600 font-medium text-xs transition-colors focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedPostCheckMemberId || !postCheckCodeUpdate.trim()}
                className="px-4 py-2 rounded-xl bg-[#0284c7] hover:bg-[#0369a1] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs shadow-md shadow-sky-900/10 transition-all focus:outline-none flex items-center gap-1.5"
                onClick={() => {
                  if (!selectedPostCheckMemberId || !postCheckTaskId) return;
                  if (!postCheckCodeUpdate.trim()) {
                    toast.error("Please enter what is updated in this code!");
                    return;
                  }
                  dispatch(
                    checkingAfterImplementationReq({
                      id: postCheckTaskId,
                      SuportTeamId: selectedPostCheckMemberId,
                      codeUpdate: postCheckCodeUpdate.trim(),
                      description: postCheckDescription || "",
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === "fulfilled") {
                      toast.success("Task moved to Checking after Implementation successfully!");
                      if (loggedInUserId) {
                        dispatch(fetchComplaintData(loggedInUserId));
                      }
                    } else {
                      toast.error(res.payload || "Failed to update task stage.");
                    }
                  }).catch(() => {
                    toast.error("An error occurred while updating stage.");
                  });

                  setIsPostCheckModalOpen(false);
                  setSelectedPostCheckMemberId("");
                  setPostCheckTaskId(null);
                  setPostCheckDescription("");
                  setPostCheckCodeUpdate("");
                }}
              >
                <CheckCircle2 size={14} />
                <span>Assign & Send</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CustomerReq;
